<template>
    <div v-if="!isLoading">
        <left-menu v-if="showLeftMenu" @changeComp="handleChangeComp" />
        <app-updater />
        <branch-picker v-if="pickBranch" />
        <router-view></router-view>
    </div>
    <div v-else>
        <p>Loading...</p>
    </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth'; // Import the Pinia store
import { verify_token } from '@/api/api.js';
import LeftMenu from '@/components/LeftMenu.vue';
import BranchPicker from '@/components/branchPicker.vue';
import AppUpdater from '@/components/AppUpdater.vue';

export default {
    name: 'App',
    components: {
        LeftMenu,
        BranchPicker,
        AppUpdater
    },
    data() {
        return {
            isDarkMode: false,
            isLoading: true, // Add loading state
            pickBranch: false
        };
    },
    computed: {
        showLeftMenu() {
            return this.$route.meta.showLeftMenu || false;
        },
    },
    methods: {
        handleChangeComp() {
            this.$cookies.remove('c_id');
            this.pickBranch = true;
        },
        checkBrowserDarkMode() {
            let dMode = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
            if (dMode === true) {
                localStorage.removeItem("darkMode");
            }
            return dMode;
        },
        // Initialize dark mode based on localStorage or browser settings
        initializeDarkMode() {
            const savedMode = localStorage.getItem("darkMode");
            if (savedMode !== null) {
                this.isDarkMode = savedMode === "true";
            } else {
                this.isDarkMode = this.checkBrowserDarkMode();
            }
            this.applyDarkMode();
        },
        // Toggle dark mode and save the preference
        toggleDarkMode() {
            this.isDarkMode = !this.isDarkMode;
            this.applyDarkMode();
            localStorage.setItem("darkMode", this.isDarkMode.toString());
        },
        // Apply the dark mode class to the HTML root element
        applyDarkMode() {
            const rootElement = document.documentElement;
            if (this.isDarkMode) {
                rootElement.classList.add("dark");
            } else {
                rootElement.classList.remove("dark");
            }
        },
        handleSystemThemeChange(e) {
            if (!localStorage.getItem("darkMode")) {
                this.isDarkMode = e.matches; // `true` for dark, `false` for light
                this.applyDarkMode();
            }
        },
    },
    beforeDestroy() {
        // Clean up the event listener
        if (window.matchMedia) {
            const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
            mediaQuery.removeEventListener("change", this.handleSystemThemeChange);
        }
    },
    async created() {
        const authStore = useAuthStore(); // Access the Pinia store
        const userToken = this.$cookies.get('userToken');
        if (this.showLeftMenu && userToken) {
            try {
                const companyID = this.$cookies.get('c_id') || null;
                const userData = await verify_token(userToken, companyID, 'header');
                if (userData && userData?.selectBranch) {
                    this.pickBranch = true;
                } else {
                    authStore.setAuthData(userData); // Store the fetched data in Pinia
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
                this.$router.push('/login'); // Redirect to login on error
            } finally {
                this.isLoading = false; // Data is ready or error occurred
            }
        } else if (this.showLeftMenu && !userToken) {
            this.$router.push('/login'); // No token, redirect to login
            this.isLoading = false;
        } else {
            this.isLoading = false; // No fetch needed
        }
    },
    mounted() {
        this.checkBrowserDarkMode();
        this.initializeDarkMode();
        
        if (window.matchMedia) {
            const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
            mediaQuery.addEventListener("change", this.handleSystemThemeChange);
        }
        
        const authStore = useAuthStore(); // Access the Pinia store again
        const initiateCall = () => {
            if (this.$router.currentRoute.value.path.includes('/admin')) {
                return;
            }
            initialize(document, "Freshchat-js-sdk");
        };
        
        const initialize = (i, t) => {
            var e;
            if (i.getElementById(t)) {
                initFreshChat();
            } else {
                (e = i.createElement("script")).id = t;
                e.async = true;
                e.src = import.meta.env.VITE_FRESHCHAT_WIDGET_URL;
                e.onload = initFreshChat;
                i.head.appendChild(e);
            }
        };
        
        const initFreshChat = () => {
            let init = {
                token: import.meta.env.VITE_FRESHCHAT_TOKEN,
                host: import.meta.env.VITE_FRESHCHAT_URL,
            };
            if (authStore.user != null && authStore.user.myself != null) {
                init.externalId = authStore.user.myself?.id ?? null;
                init.firstName = authStore.user.myself?.username ?? null;
                init.email = authStore.user.myself?.email ?? null;
                init.properties = {
                    company: authStore.user?.branchName ?? null,
                    companyID: authStore.user?.companyID_global ?? null,
                    position: authStore.user.myself?.position ?? null,
                    permissions: authStore.user?.permissions ?? null,
                };
            }
            window.fcWidget.init(init);
        };
        
        setTimeout(async () => {
            if (this.$cookies.get('userToken') != null && !this.$router.currentRoute.value.path.includes('/admin')) {
                const companyID = this.$cookies.get('c_id') || null;
                const opravnenia = await verify_token(this.$cookies.get('userToken'), companyID, 'header');
                if (opravnenia && opravnenia?.selectBranch) {
                    console.log("Select branch required, redirecting...");
                    /*let prev = this.$route.path;
                    this.$router.push({name:'Branches', query: { prevRoute: prev } });*/
                    this.pickBranch = true;
                } else {
                    authStore.setAuthData(opravnenia); // Store the fetched data in Pinia
                    this.$nextTick(() => {
                        initiateCall();
                    });
                }
            } else {
                this.$nextTick(() => {
                    initiateCall();
                });
            }
        }, 1000);
    },
};
</script>

<style>
#app {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: #2c3e50;
}
</style>